<template>
  <teleport to="#app">
    <oxd-dialog
      class="remote-work-dialog"
      :style="{width: '900px', maxWidth: '95vw'}"
      @update:show="onClose"
    >
      <div class="remote-work-dialog-header">
        <oxd-text tag="h6">Remote Work Request #{{ requestId }}</oxd-text>
        <oxd-button
          label="Close"
          display-type="ghost"
          :disabled="isSaving"
          @click="onClose"
        />
      </div>
      <oxd-divider />
      <div class="remote-work-dialog-body">
        <oxd-text v-if="isLoading">Loading request details…</oxd-text>
        <oxd-text v-if="error" class="remote-work-error" role="alert">
          {{ error }}
        </oxd-text>
        <oxd-button
          v-if="!request && error"
          label="Try again"
          display-type="ghost"
          @click="loadRequest"
        />
        <template v-if="request && !isLoading">
          <oxd-text class="remote-work-status">
            {{ statusLabel(request.status) }}
          </oxd-text>
          <oxd-text v-if="request.revisionOf" class="remote-work-help">
            This application revises request #{{ request.revisionOf }}.
          </oxd-text>
          <dl class="remote-work-details">
            <div>
              <dt>Employee</dt>
              <dd>{{ employeeName }}</dd>
            </div>
            <div>
              <dt>Effective dates</dt>
              <dd>
                {{ displayDate(request.fromDate) }} –
                {{ displayDate(request.toDate) }}
              </dd>
            </div>
            <div class="remote-work-details-wide">
              <dt>Remote work schedule</dt>
              <dd>{{ schedule }}</dd>
            </div>
            <div class="remote-work-details-wide">
              <dt>Remote work location</dt>
              <dd class="remote-work-preserve-lines">{{ address }}</dd>
            </div>
            <div v-if="request.scheduleNotes" class="remote-work-details-wide">
              <dt>Schedule details</dt>
              <dd class="remote-work-preserve-lines">
                {{ request.scheduleNotes }}
              </dd>
            </div>
            <div>
              <dt>Employee signature</dt>
              <dd>{{ request.signature }}</dd>
            </div>
            <div>
              <dt>Signed on</dt>
              <dd>{{ displayTimestamp(request.signedAt) }}</dd>
            </div>
          </dl>
          <details
            v-if="request.agreementTerms && request.agreementTerms.length"
            class="remote-work-signed-terms"
          >
            <summary>Signed remote work agreement</summary>
            <oxd-text
              v-if="request.agreementAcknowledgment"
              class="remote-work-help"
            >
              {{ request.agreementAcknowledgment }}
            </oxd-text>
            <ol>
              <li v-for="(term, index) in request.agreementTerms" :key="index">
                {{ term }}
              </li>
            </ol>
          </details>
          <oxd-text v-else class="remote-work-help">
            The employee acknowledged the Remote Work Policy, including approved
            hours and location, communication, dependent care, secure internet,
            confidentiality, record retention, and management's right to change
            or end the arrangement.
          </oxd-text>
          <oxd-text
            v-if="request.status === 'pending_ed'"
            class="remote-work-help"
          >
            Supervisor review is complete. This arrangement still needs final
            Executive Director approval before it can begin.
          </oxd-text>
          <oxd-text
            v-if="request.status === 'pending_management'"
            class="remote-work-help"
          >
            Supervisor review is complete. This arrangement is awaiting
            {{
              request.remainingManagementReviewerEmpNumbers?.length ||
              'the required'
            }}
            management approval(s), followed by final Executive Director
            approval.
          </oxd-text>
          <div v-if="actions.length && !action" class="remote-work-actions">
            <oxd-button
              v-for="availableAction in actions"
              :key="availableAction"
              :label="actionLabel(availableAction)"
              :display-type="
                availableAction === 'approve' ? 'secondary' : 'ghost'
              "
              @click="selectAction(availableAction)"
            />
          </div>
          <oxd-form
            v-if="action"
            :key="action"
            :loading="isSaving"
            class="remote-work-action-form"
            @submit-valid="submitAction"
          >
            <oxd-text type="card-title">{{ actionLabel(action) }}</oxd-text>
            <oxd-text class="remote-work-help">{{ actionMessage }}</oxd-text>
            <oxd-form-row>
              <oxd-input-field
                v-model="comment"
                type="textarea"
                :label="commentRequired ? 'Reason' : 'Comment (optional)'"
                :rules="commentRules"
                :required="commentRequired"
              />
            </oxd-form-row>
            <oxd-form-actions>
              <oxd-button
                type="button"
                label="Back"
                display-type="ghost"
                :disabled="isSaving"
                @click="action = null"
              />
              <submit-button
                :label="actionLabel(action)"
                :disabled="isSaving"
              />
            </oxd-form-actions>
          </oxd-form>
          <oxd-divider />
          <oxd-text type="card-title">Request history</oxd-text>
          <ol
            v-if="request.history && request.history.length"
            class="remote-work-history"
          >
            <li v-for="event in request.history" :key="event.id">
              <oxd-text class="remote-work-history-title">
                {{ historyAction(event.action) }}
                <template v-if="event.actorName">
                  · {{ event.actorName }}</template
                >
              </oxd-text>
              <oxd-text class="remote-work-help">
                {{ displayTimestamp(event.createdAt) }}
                <template v-if="event.toStatus">
                  · {{ statusLabel(event.toStatus) }}</template
                >
              </oxd-text>
              <oxd-text v-if="event.comment" class="remote-work-preserve-lines">
                {{ event.comment }}
              </oxd-text>
            </li>
          </ol>
          <oxd-text v-else class="remote-work-help"
            >No history is available.</oxd-text
          >
        </template>
      </div>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {
  REMOTE_WORK_PATH,
  permittedRemoteWorkActions,
  remoteWorkActionLabels,
  remoteWorkError,
  remoteWorkSchedule,
  remoteWorkStatuses,
} from '@/orangehrmLeavePlugin/util/remote-work';

export default {
  name: 'RemoteWorkDetails',
  components: {'oxd-dialog': OxdDialog},
  props: {
    requestId: {type: Number, required: true},
  },
  emits: ['close', 'changed', 'revise'],
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, REMOTE_WORK_PATH);
    http.setIgnorePath(REMOTE_WORK_PATH);
    const {jsDateFormat} = useDateFormat();
    return {http, jsDateFormat};
  },
  data() {
    return {
      request: null,
      error: '',
      isLoading: false,
      isSaving: false,
      action: null,
      comment: '',
    };
  },
  computed: {
    actions() {
      return permittedRemoteWorkActions(this.request);
    },
    employeeName() {
      return [
        this.request?.employee?.firstName,
        this.request?.employee?.lastName,
      ]
        .filter(Boolean)
        .join(' ');
    },
    schedule() {
      return remoteWorkSchedule(this.request);
    },
    address() {
      const address = this.request.address;
      return [
        address.line1,
        address.line2,
        [address.city, address.state, address.postalCode]
          .filter(Boolean)
          .join(', '),
        address.country,
      ]
        .filter(Boolean)
        .join('\n');
    },
    commentRequired() {
      return ['reject', 'revoke', 'request_revision'].includes(this.action);
    },
    commentRules() {
      return this.commentRequired
        ? [required, shouldNotExceedCharLength(1000)]
        : [shouldNotExceedCharLength(1000)];
    },
    actionMessage() {
      switch (this.action) {
        case 'approve':
          if (this.request.status === 'pending_management') {
            return 'Record your management approval. This arrangement remains pending until all required reviewers and the Executive Director approve.';
          }
          return this.request.status === 'pending_supervisor'
            ? 'Approve this schedule and location for any required management review and Executive Director review. The arrangement will remain pending until final approval.'
            : 'Give final approval for the signed remote work arrangement shown above.';
        case 'reject':
          return 'Reject this application and explain the decision to the employee.';
        case 'request_revision':
          return 'Tell the employee what needs to change. They will submit a revised, signed application for approval.';
        case 'revoke':
          return 'Withdraw approval for this arrangement. Explain why it is ending and communicate the change to the employee.';
        case 'cancel':
          return 'Cancel this request or arrangement. If it is a proposed revision, any existing approved arrangement remains in place.';
        default:
          return '';
      }
    },
  },
  beforeMount() {
    this.loadRequest();
  },
  methods: {
    async loadRequest() {
      this.isLoading = true;
      this.error = '';
      try {
        const {data} = await this.http.get(this.requestId);
        this.request = data.data;
      } catch (error) {
        this.request = null;
        this.error = remoteWorkError(
          error,
          'This request could not be loaded. Please try again.',
        );
      } finally {
        this.isLoading = false;
      }
    },
    displayDate(date) {
      return formatDate(parseDate(date), this.jsDateFormat) || date;
    },
    displayTimestamp(timestamp) {
      return timestamp
        ? timestamp.replace('T', ' ').replace(/Z$/, ' UTC')
        : '—';
    },
    statusLabel(status) {
      return remoteWorkStatuses[status] || status;
    },
    actionLabel(action) {
      if (action === 'revise' && this.request?.status === 'approved') {
        return 'Request a change';
      }
      return remoteWorkActionLabels[action] || action;
    },
    historyAction(action) {
      const labels = {
        submit: 'Application submitted',
        submitted: 'Application submitted',
        create: 'Application submitted',
        approve: 'Approved',
        management_approve: 'Management approval recorded',
        reject: 'Rejected',
        cancel: 'Cancelled',
        revoke: 'Approval withdrawn',
        revise: 'Revised application submitted',
        request_revision: 'Changes requested',
        supersede: 'Replaced by a revised request',
      };
      return labels[action] || action.replace(/_/g, ' ');
    },
    selectAction(action) {
      if (!this.actions.includes(action)) return;
      if (action === 'revise') {
        this.$emit('revise', this.request);
        return;
      }
      this.comment = '';
      this.action = action;
      this.error = '';
    },
    async submitAction() {
      if (this.isSaving || !this.actions.includes(this.action)) return;
      this.isSaving = true;
      this.error = '';
      try {
        await this.http.update(this.requestId, {
          action: this.action,
          comment: this.comment.trim(),
        });
        this.action = null;
        this.comment = '';
        this.$toast.updateSuccess();
        this.$emit('changed');
        await this.loadRequest();
      } catch (error) {
        this.error = remoteWorkError(
          error,
          'This action could not be completed. The request may have changed. Close and reopen it to review its current status.',
        );
      } finally {
        this.isSaving = false;
      }
    },
    onClose() {
      if (!this.isSaving) this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.remote-work-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.remote-work-dialog-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 0.5rem;
}
.remote-work-status {
  font-weight: 700;
  margin-bottom: 1rem;
}
.remote-work-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  font-family: $oxd-font-family;
  font-size: 0.875rem;
  color: $oxd-interface-gray-darken-1-color;
  dt {
    font-weight: 700;
    margin-bottom: 0.35rem;
  }
  dd {
    margin: 0;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
}
.remote-work-details-wide {
  grid-column: 1 / -1;
}
.remote-work-preserve-lines {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.remote-work-signed-terms {
  margin: 1rem 0;
  font-family: $oxd-font-family;
  font-size: 0.875rem;
  color: $oxd-interface-gray-darken-1-color;
  line-height: 1.6;
  summary {
    cursor: pointer;
    font-weight: 600;
  }
  li + li {
    margin-top: 0.75rem;
  }
}
.remote-work-help {
  margin: 0.75rem 0;
  font-size: 0.875rem;
}
.remote-work-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1.5rem 0;
}
.remote-work-action-form {
  padding: 1rem;
  border-radius: 0.75rem;
  background: $oxd-background-pastel-white-color;
}
.remote-work-error {
  margin: 1rem 0;
  padding: 1rem;
  border-left: 3px solid $oxd-feedback-danger-color;
}
.remote-work-history {
  list-style: none;
  padding-left: 0.75rem;
  li {
    border-left: 2px solid $oxd-interface-gray-lighten-2-color;
    padding: 0 0 1.25rem 1rem;
  }
}
.remote-work-history-title {
  font-weight: 600;
}
@media (max-width: 600px) {
  .remote-work-details {
    grid-template-columns: 1fr;
  }
}
</style>
